import { useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Clock, Flame, Instagram, MapPin, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { ORIGIN, pathFor } from "../lib/routing";

export const GALLERY = [
  "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/7f7f3a4f-1933-402a-81fe-7a1d7c5879a5.jpeg",
  "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/63d6be53-7312-4840-9a52-438773afea60.jpeg",
  "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/9e76fcc5-b86e-4da0-8e5f-7bbde5dd62af.jpeg",
  "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/016ece4f-bd00-4f37-b10a-7a45657f9e1a.jpeg",
];

const INSTAGRAM_POSTS = [
  { url: "https://www.instagram.com/p/DO9kLboEcrS/", label: "Asado Xperience en Palermo Soho" },
  { url: "https://www.instagram.com/p/DUI-c_4kZXr/", label: "Experiencia de asado argentino" },
  { url: "https://www.instagram.com/p/DYQB3ULPhqT/", label: "Parrilla y cortes premium" },
  { url: "https://www.instagram.com/p/DbdhO2ThOOw/", label: "Mesa y asado en Buenos Aires" },
];

/**
 * Title, description y JSON-LD de la ruta. El canonical y los hreflang los maneja
 * RouteSeo a nivel global; acá no se tocan.
 */
function SeoMetadata() {
  const { t, lang } = useI18n();

  useEffect(() => {
    const title = t.palermo.seo_title;
    const description = t.palermo.seo_description;
    const canonical = `${ORIGIN}${pathFor("palermo", lang)}`;
    const previousTitle = document.title;
    const previousMeta = new Map();
    document.title = title;

    const setMeta = (selector, attribute, value) => {
      let element = document.head.querySelector(selector);
      if (element && !previousMeta.has(selector)) previousMeta.set(selector, element.getAttribute("content"));
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, selector.includes("property") ? selector.match(/\[property='([^']+)'\]/)?.[1] : selector.match(/\[name='([^']+)'\]/)?.[1]);
        previousMeta.set(selector, null);
        document.head.appendChild(element);
      }
      element.setAttribute("content", value);
    };

    setMeta("meta[name='description']", "name", description);
    setMeta("meta[property='og:title']", "property", title);
    setMeta("meta[property='og:description']", "property", description);
    setMeta("meta[property='og:url']", "property", canonical);
    setMeta("meta[name='twitter:title']", "name", title);
    setMeta("meta[name='twitter:description']", "name", description);

    // Próxima ocurrencia (fecha rodante) para que el evento nunca quede vencido.
    // La experiencia es en Buenos Aires (UTC-3, sin horario de verano); cena 19:30–22:00.
    const upcoming = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
    const ymd = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Argentina/Buenos_Aires",
      year: "numeric", month: "2-digit", day: "2-digit",
    }).format(upcoming);

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FoodEvent",
      name: title,
      description,
      url: canonical,
      inLanguage: lang,
      image: [`${ORIGIN}/og-cover.jpg`],
      startDate: `${ymd}T19:30:00-03:00`,
      endDate: `${ymd}T22:00:00-03:00`,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: "Asado Xperience",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Palermo Soho",
          addressRegion: "Buenos Aires",
          addressCountry: "AR",
        },
      },
      organizer: { "@type": "Person", name: "Diego", url: canonical },
      performer: { "@type": "Person", name: "Diego" },
      offers: {
        "@type": "Offer",
        price: "65",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: `${ORIGIN}${pathFor("home", lang)}#book`,
        validFrom: new Date().toISOString().slice(0, 10),
      },
    };

    // El HTML pre-renderizado ya trae este mismo bloque; lo reemplazamos en vez de sumar un duplicado.
    document.querySelectorAll("script[data-route-schema]").forEach((el) => el.remove());
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.dataset.routeSchema = "true";
    schema.textContent = JSON.stringify(structuredData);
    document.head.appendChild(schema);

    return () => {
      document.title = previousTitle;
      previousMeta.forEach((content, selector) => {
        const element = document.head.querySelector(selector);
        if (!element) return;
        if (content === null) element.remove();
        else element.setAttribute("content", content);
      });
      document.querySelector("script[data-route-schema]")?.remove();
    };
  }, [t, lang]);

  return null;
}

// Galería e Instagram se reutilizan en la landing del curso (CursoParrillero.jsx).
export function GalleryCarousel({ images = GALLERY, alts }) {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  const altList = alts || t.palermo.gallery_alts;
  const previous = () => setActive((current) => (current - 1 + images.length) % images.length);
  const next = () => setActive((current) => (current + 1) % images.length);

  return (
    <div className="relative" aria-label={t.palermo.gallery_label} role="region">
      <div className="relative overflow-hidden aspect-[4/5] md:aspect-[16/10]">
        <img src={images[active]} alt={altList[active]} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charred/60 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
          <span className="overline">{String(active + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span>
          <div className="flex gap-2">
            <button type="button" onClick={previous} aria-label={t.palermo.gallery_prev} className="h-11 w-11 border border-white/30 bg-charred/60 text-white flex items-center justify-center hover:border-ember hover:text-ember transition-colors">
              <ChevronLeft size={18} />
            </button>
            <button type="button" onClick={next} aria-label={t.palermo.gallery_next} className="h-11 w-11 border border-white/30 bg-charred/60 text-white flex items-center justify-center hover:border-ember hover:text-ember transition-colors">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4" role="tablist" aria-label={t.palermo.gallery_label}>
        {images.map((src, index) => (
          <button key={src} type="button" role="tab" aria-selected={active === index} aria-label={altList[index]} onClick={() => setActive(index)} className={`h-1 flex-1 transition-colors ${active === index ? "bg-ember" : "bg-white/20 hover:bg-white/50"}`} />
        ))}
      </div>
    </div>
  );
}

export function InstagramShowcase() {
  const { t } = useI18n();
  const [active, setActive] = useState(0);

  const previous = () => setActive((current) => (current - 1 + INSTAGRAM_POSTS.length) % INSTAGRAM_POSTS.length);
  const next = () => setActive((current) => (current + 1) % INSTAGRAM_POSTS.length);
  const post = INSTAGRAM_POSTS[active];

  return (
    <section className="py-24 md:py-32 border-y border-white/5 bg-[#0a0a0a]" aria-labelledby="instagram-title">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.palermo.ig_overline}</span>
          <h2 id="instagram-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{t.palermo.ig_title}</h2>
          <p className="mt-6 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{t.palermo.ig_body}</p>
          <a href="https://www.instagram.com/asadoxperience/" target="_blank" rel="noreferrer" className="btn-ghost mt-8 inline-flex"><Instagram size={15} /> @asadoxperience <ArrowRight size={14} /></a>
        </div>
        <div className="lg:col-span-7">
          <div className="relative flex justify-center min-h-[540px]">
            <iframe
              key={post.url}
              src={`${post.url}embed/`}
              title={post.label}
              loading="lazy"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              className="w-full max-w-[540px] min-h-[540px] bg-white border-0"
            />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex items-center gap-3 bg-charred px-3 py-2 border border-white/10">
              <button type="button" onClick={previous} aria-label={t.palermo.gallery_prev} className="h-9 w-9 border border-white/20 text-[#F5F3F0] flex items-center justify-center hover:border-ember hover:text-ember transition-colors"><ChevronLeft size={16} /></button>
              <span className="overline whitespace-nowrap">{String(active + 1).padStart(2, "0")} / {String(INSTAGRAM_POSTS.length).padStart(2, "0")}</span>
              <button type="button" onClick={next} aria-label={t.palermo.gallery_next} className="h-9 w-9 border border-white/20 text-[#F5F3F0] flex items-center justify-center hover:border-ember hover:text-ember transition-colors"><ChevronRight size={16} /></button>
            </div>
          </div>
          <p className="text-center text-[#A3A3A3] text-xs uppercase tracking-[0.18em] mt-10">{post.label}</p>
        </div>
      </div>
    </section>
  );
}

export default function PalermoExperience() {
  const { t, lang } = useI18n();
  const p = t.palermo;
  const home = pathFor("home", lang);

  return (
    <>
      <SeoMetadata />
      <main>
        <section className="relative min-h-[78vh] flex items-end overflow-hidden" aria-labelledby="palermo-title">
          <img src={GALLERY[0]} alt={p.hero_alt} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charred via-charred/75 to-charred/25" />
          <div className="absolute inset-0 grain" />
          <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-12 pb-20 pt-40">
            <span className="overline flex items-center gap-3"><span className="divider-gold" />{p.hero_overline}</span>
            <h1 id="palermo-title" className="font-display font-light tracking-tight text-[#F5F3F0] mt-6 text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] max-w-5xl">{p.hero_title_1}<br /><em className="italic text-ember">{p.hero_title_2}</em></h1>
            <p className="max-w-2xl mt-8 text-base md:text-lg text-[#D1CEC9] leading-relaxed">{p.hero_subtitle}</p>
            <Link to={home} className="btn-primary mt-10 inline-flex"><Flame size={14} /> {p.hero_cta}</Link>
          </div>
        </section>

        <section className="py-24 md:py-36" aria-labelledby="authentic-title">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-center">
            <div className="md:col-span-7">
              <span className="overline flex items-center gap-3"><span className="divider-gold" />{p.intro_overline}</span>
              <h2 id="authentic-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{p.intro_title}</h2>
              <p className="mt-8 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{p.intro_p1}</p>
              <p className="mt-5 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{p.intro_p2}</p>
              <p className="mt-5 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{p.intro_p3}</p>
            </div>
            <div className="md:col-span-5"><GalleryCarousel /></div>
          </div>
        </section>

        <section className="py-24 md:py-32 bg-[#0a0a0a] border-y border-white/5" aria-labelledby="menu-title">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <span className="overline flex items-center gap-3"><span className="divider-gold" />{p.menu_overline}</span>
            <h2 id="menu-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{p.menu_title}</h2>
            <div className="grid md:grid-cols-2 gap-x-16 gap-y-10 mt-16">
              {p.menu_items.map(([number, title, body]) => (
                <article key={number} className="border-t border-white/10 pt-5">
                  <span className="font-display text-4xl text-ember italic">{number}</span>
                  <h3 className="font-display text-3xl text-[#F5F3F0] mt-3">{title}</h3>
                  <p className="text-[#A3A3A3] mt-3 leading-relaxed">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 md:py-36" aria-labelledby="details-title">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <span className="overline flex items-center gap-3"><span className="divider-gold" />{p.details_overline}</span>
              <h2 id="details-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{p.details_title}</h2>
              <p className="mt-6 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{p.details_body}</p>
            </div>
            <div className="space-y-5 md:pt-12">
              <div className="flex gap-4 border-t border-white/10 pt-5"><MapPin className="text-ember shrink-0" size={18} /><p className="text-[#F5F3F0]">{p.details_location}</p></div>
              <div className="flex gap-4 border-t border-white/10 pt-5"><Clock className="text-ember shrink-0" size={18} /><p className="text-[#F5F3F0]">{p.details_hours}</p></div>
              <div className="flex gap-4 border-t border-white/10 pt-5"><Users className="text-ember shrink-0" size={18} /><p className="text-[#F5F3F0]">{p.details_capacity}</p></div>
              <div className="flex gap-4 border-t border-white/10 pt-5"><Flame className="text-ember shrink-0" size={18} /><p className="text-[#F5F3F0]">{p.details_price}</p></div>
            </div>
          </div>
        </section>

        <InstagramShowcase />

        <section id="reservar" className="py-24 md:py-32 bg-[#0a0a0a] text-center" aria-labelledby="cta-title">
          <div className="max-w-3xl mx-auto px-6">
            <span className="overline">{p.cta_overline}</span>
            <h2 id="cta-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{p.cta_title}</h2>
            <p className="mt-6 text-[#A3A3A3] text-lg leading-relaxed">{p.cta_body}</p>
            <Link to={`${home}#book`} className="btn-primary mt-10 inline-flex"><Flame size={14} /> {p.cta_button} <ArrowRight size={14} /></Link>
          </div>
        </section>
      </main>
    </>
  );
}
